// Promise-based request/response helper over the WS channel.
//
// Backend echoes the request_id on every reply (see
// `internal/websocket/handler.go` — `reply.RequestId = env.RequestId`), so
// we can correlate one-shot actions (e.g. set priority, reveal node,
// run enhancement) without a Zustand store.
//
// Use this when a single user action → single server response → single
// resolve. For ongoing state (lists, subscriptions), prefer a store with
// the standard `registerXxxHandlers()` pattern — this helper would leak
// handlers if used for long-lived subscriptions.

import { createEnvelope } from '@/lib/protobuf/codec';
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import { friendlyMessage, limitValues, type WireError } from '@/lib/errors/catalog';
import { hasWireError, wireErrorFromEnvelope } from '@/lib/errors/fromEnvelope';
import { wsManager } from './WebSocketManager';
import { DEFAULT_REQUEST_TIMEOUT_MS, timeoutError } from './requestTimeout';

/**
 * RequestError is the typed rejection a `sendRequest` promise throws when the
 * server returns an error envelope. Its `.message` is the warm, SAFE message
 * (resolved through the R7 catalog) — never a raw internal string — so existing
 * `catch (e) { e.message }` sites are warm by default. `.wire` carries the typed
 * code so callers can route through `toastStore.showError(e.wire)` and render a
 * "Try again" affordance from the retryable hint.
 */
export class RequestError extends Error {
  readonly wire: WireError;
  constructor(wire: WireError) {
    super(friendlyMessage(wire.code, wire.message, limitValues(wire)));
    this.name = 'RequestError';
    this.wire = wire;
  }
}

export interface SendRequestOptions {
  /** Abort before the response arrives; rejects with AbortError. */
  signal?: AbortSignal;
  /** Override the 30s default. */
  timeoutMs?: number;
  /**
   * Caller-minted request id. Also passed to the transport as `correlationId`,
   * so the caller can `wsManager.withdrawQueued(requestId)`.
   */
  requestId?: string;
  /**
   * 'call' (default): the timeout starts when sendRequest is called — unchanged for every existing caller.
   * 'sent': the timeout starts only when the transport reports 'sent'. A 'queued' frame arms NO timer, so
   * a caller using 'sent' must own queued frames through `onQueued` (withdraw + abort, or accept no timeout).
   */
  timeoutFrom?: 'call' | 'sent';
  /** Called synchronously when the transport queued the frame instead of sending it. */
  onQueued?: (requestId: string) => void;
}

// MessageEnvelope.payload is a protobuf-es discriminated union:
//   { case: 'fooResponse', value: <Foo message> } | { case: undefined, value?: undefined }
// This narrowing keeps callers in the typed world without per-call casts.
type PayloadValueFor<C extends string> = Extract<
  MessageEnvelope['payload'],
  { case: C }
> extends { value: infer V }
  ? V
  : never;

/**
 * Send a request envelope and resolve with the first matching response.
 *
 * Matching rules:
 *   - Response's `requestId` must equal the outgoing envelope's `requestId`.
 *   - Response's payload case must equal `responseCase`.
 *   - If the server returns an error envelope for this request
 *     (envelope.error set), the promise rejects with an Error.
 */
export function sendRequest<C extends string>(
  payload: MessageEnvelope['payload'],
  responseCase: C,
  options?: SendRequestOptions,
): Promise<PayloadValueFor<C>> {
  const envelope = createEnvelope(payload, options?.requestId);
  const requestId = envelope.requestId;
  const timeoutMs = options?.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;

  return new Promise<PayloadValueFor<C>>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const finish = () => {
      settled = true;
      offResponse();
      offError();
      if (timer !== undefined) clearTimeout(timer);
      if (options?.signal) {
        options.signal.removeEventListener('abort', onAbort);
      }
    };

    const offResponse = wsManager.onMessage(responseCase, (env) => {
      if (settled) return;
      if (env.requestId !== requestId) return;
      if (env.payload.case !== responseCase) return;
      finish();
      resolve(env.payload.value as PayloadValueFor<C>);
    });

    // Errors arrive on either carrier (typed errorEvent payload OR the legacy
    // envelope.error), correlated by requestId. onAnyMessage sees every
    // deserialized envelope. We reject with a typed RequestError whose message
    // is the warm/safe catalog string — never the raw wire string.
    const offError = wsManager.onAnyMessage((env) => {
      if (settled) return;
      if (env.requestId !== requestId) return;
      if (!hasWireError(env)) return;
      const wire = wireErrorFromEnvelope(env);
      if (!wire) return;
      finish();
      reject(new RequestError(wire));
    });

    const armTimer = () => {
      timer = setTimeout(() => {
        if (settled) return;
        finish();
        reject(timeoutError(`request ${responseCase}`));
      }, timeoutMs);
    };
    if (options?.timeoutFrom !== 'sent') armTimer();

    const onAbort = () => {
      if (settled) return;
      finish();
      const err = new Error('request aborted');
      err.name = 'AbortError';
      reject(err);
    };
    if (options?.signal) {
      if (options.signal.aborted) {
        // Don't even send — match fetch() semantics.
        finish();
        const err = new Error('request aborted');
        err.name = 'AbortError';
        reject(err);
        return;
      }
      options.signal.addEventListener('abort', onAbort, { once: true });
    }

    const outcome = wsManager.sendEnvelope(envelope, { correlationId: requestId });
    if (outcome === 'sent') {
      if (options?.timeoutFrom === 'sent') armTimer();
    } else {
      options?.onQueued?.(requestId); // may abort synchronously; onAbort → finish() clears any armed timer
    }
  });
}
